import React, { useEffect, useState } from "react"
import styled from "styled-components"

import {
  ArtsyMarkIcon,
  Box,
  Button,
  ChevronIcon,
  Flex,
  Link,
  Spacer,
  Text,
  color,
  space,
  themeProps,
} from "@artsy/palette"

import { useSystemContext } from "v2/Artsy/SystemContext"
import { SearchBarQueryRenderer as SearchBar } from "v2/Components/Search/SearchBar"

import {
  DropDownNavMenu,
  MobileNavMenu,
  MobileToggleIcon,
  MoreNavMenu,
} from "./Menus"
import { InboxNotificationCountQueryRenderer as InboxNotificationCount } from "./Menus/MobileNavMenu/InboxNotificationCount"

import { ModalType } from "v2/Components/Authentication/Types"
import { MenuLinkData, menuData } from "v2/Components/NavBar/menuData"
import { openAuthModal } from "v2/Utils/openAuthModal"
import { userHasLabFeature } from "v2/Utils/user"

import { NavItem } from "./NavItem"

import { ContextModule, Intent } from "@artsy/cohesion"
import { AnalyticsSchema } from "v2/Artsy"
import { track, useTracking } from "v2/Artsy/Analytics"
import Events from "v2/Utils/Events"
import { useMatchMedia } from "v2/Utils/Hooks/useMatchMedia"
import { LoggedInActionsQueryRenderer as LoggedInActions } from "./LoggedInActions"

const SkipLink = styled.a`
  display: block;
  position: absolute;
  top: -100%;
  left: 0;
  color: ${color("black100")};
  background-color: ${color("black10")};

  &:focus {
    position: relative;
    top: 0;
    padding: ${space(1)}px;
  }
`

export const NavBar: React.FC = track(
  {
    flow: AnalyticsSchema.Flow.Header,
    context_module: AnalyticsSchema.ContextModule.Header,
  },
  {
    dispatch: data => Events.postEvent(data),
  }
)(() => {
  const { trackEvent } = useTracking()
  const { mediator, user } = useSystemContext()
  const [showMobileMenu, toggleMobileNav] = useState(false)
  const xs = useMatchMedia(themeProps.mediaQueries.xs)
  const sm = useMatchMedia(themeProps.mediaQueries.sm)
  const isMobile = xs || sm
  const isLoggedIn = Boolean(user)
  const conversationsEnabled = userHasLabFeature(
    user,
    "User Conversations View"
  )
  // ToDo: replace check for conversationsEnabled with check for user when ready to launch
  const showNotificationCount =
    isLoggedIn && !showMobileMenu && conversationsEnabled

  const {
    links: [artworks, artists],
  } = menuData

  // Close mobile menu if dragging window from small size to desktop
  useEffect(() => {
    if (!isMobile) {
      toggleMobileNav(false)
    }
  }, [isMobile])

  /**
   * Check to see if we're clicking a link that lives within the new app shell
   * and close the navbar.
   *
   * TODO: Find a less naive way to check if route is in appshell
   */
  const handleMobileNavClick = event => {
    if (event.target?.parentNode?.href?.includes("/collect")) {
      toggleMobileNav(false)
    }
  }

  return (
    <>
      <SkipLink href="#main">
        <Text variant="text">Skip to Main Content</Text>
      </SkipLink>

      <header>
        <NavBarContainer as="nav" px={1}>
          <NavSection>
            <Link href="/" style={{ display: "flex" }}>
              <ArtsyMarkIcon height={40} width={40} />
            </Link>
          </NavSection>

          <Spacer mr={1} />

          <NavSection width="100%">
            <Box width="100%">
              <SearchBar />
            </Box>
          </NavSection>

          <Spacer mr={2} />

          {/*
            Desktop. Collapses into mobile at `sm` breakpoint.
        */}
          <NavSection display={["none", "none", "flex"]}>
            <NavSection>
              <NavItem
                label="Artworks"
                isFullScreenDropDown
                Menu={({ setIsVisible }) => {
                  return (
                    <Box>
                      <DropDownNavMenu
                        width="100vw"
                        menu={(artworks as MenuLinkData).menu}
                        contextModule={
                          AnalyticsSchema.ContextModule.HeaderArtworksDropdown
                        }
                        onClick={() => {
                          setIsVisible(false)
                        }}
                      />
                    </Box>
                  )
                }}
              >
                <Flex>
                  Artworks
                  <ChevronIcon
                    direction="down"
                    color={color("black100")}
                    height="15px"
                    width="15px"
                    top="5px"
                    left="4px"
                  />
                </Flex>
              </NavItem>

              <NavItem
                label="Artists"
                isFullScreenDropDown
                Menu={({ setIsVisible }) => {
                  return (
                    <Box>
                      <DropDownNavMenu
                        width="100vw"
                        menu={(artists as MenuLinkData).menu}
                        contextModule={
                          AnalyticsSchema.ContextModule.HeaderArtistsDropdown
                        }
                        onClick={() => {
                          setIsVisible(false)
                        }}
                      />
                    </Box>
                  )
                }}
              >
                <Flex>
                  Artists
                  <ChevronIcon
                    direction="down"
                    color={color("black100")}
                    height="15px"
                    width="15px"
                    top="5px"
                    left="4px"
                  />
                </Flex>
              </NavItem>

              <NavItem href="/auctions">Auctions</NavItem>
              <NavItem href="/articles">Editorial</NavItem>
              <NavItem
                label="More"
                Menu={() => {
                  return (
                    <Box mr={-150}>
                      <MoreNavMenu width={160} />
                    </Box>
                  )
                }}
              >
                <Flex>
                  More
                  <ChevronIcon
                    direction="down"
                    color={color("black100")}
                    height="15px"
                    width="15px"
                    top="5px"
                    left="4px"
                  />
                </Flex>
              </NavItem>
            </NavSection>

            {isLoggedIn && (
              <NavSection>
                <LoggedInActions />
              </NavSection>
            )}

            {!isLoggedIn && (
              <NavSection>
                <Spacer mr={2} />
                <Button
                  variant="secondaryOutline"
                  onClick={() => {
                    openAuthModal(mediator, {
                      mode: ModalType.login,
                      intent: Intent.login,
                      contextModule: ContextModule.header,
                    })
                  }}
                >
                  Log in
                </Button>
                <Spacer mr={1} />
                <Button
                  onClick={() => {
                    openAuthModal(mediator, {
                      mode: ModalType.signup,
                      intent: Intent.signup,
                      contextModule: ContextModule.header,
                    })
                  }}
                >
                  Sign up
                </Button>
              </NavSection>
            )}
          </NavSection>

          {/*
          Mobile. Triggers at the `sm` breakpoint.
        */}
          <NavSection display={["flex", "flex", "none"]}>
            <NavItem
              className="mobileHamburgerButton"
              onClick={() => {
                const showMenu = !showMobileMenu
                if (showMenu) {
                  trackEvent({
                    action_type: AnalyticsSchema.ActionType.Click,
                    subject:
                      AnalyticsSchema.Subject.SmallScreenMenuSandwichIcon,
                  })
                }

                toggleMobileNav(showMenu)
              }}
            >
              <Flex
                alignItems="center"
                justifyContent="center"
                width={22}
                height={22}
              >
                <MobileNavDivider />
                <MobileToggleIcon open={showMobileMenu} />
                {showNotificationCount && <InboxNotificationCount />}
              </Flex>
            </NavItem>
          </NavSection>
        </NavBarContainer>

        {showMobileMenu && (
          <>
            <MobileNavMenu
              onClose={() => toggleMobileNav(false)}
              isOpen={showMobileMenu}
              menuData={menuData}
              onNavButtonClick={handleMobileNavClick}
            />
          </>
        )}
      </header>
    </>
  )
})

export const NavBarHeight = space(6) - 1 // border offset

const NavSection = ({ children, ...props }) => (
  <Flex alignItems="center" {...props}>
    {children}
  </Flex>
)

const NavBarContainer = styled(Flex)`
  background-color: ${color("white100")};
  border-bottom: 1px solid ${color("black10")};
  position: relative;
  z-index: 3;
  height: ${NavBarHeight}px;
`

// FIXME: This needs to be cleaned up once we get proper icons
const MobileNavDivider = styled(Box)`
  border-left: 1px solid ${color("black10")};
  height: 63px;
  position: absolute;
  left: -12px;
`
